import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthorizationService } from '../../../authorization/authorization.service';
import { NGXLogger } from 'ngx-logger';
import { HttpErrorResponse } from '@angular/common/http';
import {
  EloquaSalesLeadDataAccessService,
  IEloquaSalesLead,
  IEloquaSubmitResponse,
  IEloquaProductSalesLead,
} from './eloqua-sales-lead-data-access.service';

@Injectable({
  providedIn: 'root',
})
export class EloquaSalesLeadService {
  constructor(
    private _eloquaSalesLeadDataAccessService: EloquaSalesLeadDataAccessService,
    private _authorizationService: AuthorizationService,
    private _logger: NGXLogger,
  ) {}

  public createSampleSalesLead(sampleEloquaData: IEloquaProductSalesLead): Promise<void> {
    return this._createLeadForProducts(sampleEloquaData, 219, 'sampleData');
  }

  public createMapShareLead(shareMapEloqua: IEloquaProductSalesLead): Promise<void> {
    return this._createLeadForProducts(shareMapEloqua, 222, 'shareMapData');
  }

  public contactSales(contactSalesData: IEloquaSalesLead): Promise<IEloquaSubmitResponse | undefined | null> {
    return this._createEloquaForSales(contactSalesData);
  }

  private async _createLeadForProducts(
    products: IEloquaProductSalesLead,
    eloquaFormId: number,
    data: string,
  ): Promise<void> {
    // @ts-expect-error
    products.EmailAddress = (await this._createLead()) as IProductSalesLead;
    // This is intended to be a fire and forget API call.  We do not return anything.
    try {
      await firstValueFrom(this._eloquaSalesLeadDataAccessService.createSalesLead(products, eloquaFormId, data));
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        this._logger.error('Unable to create product sales lead.', {
          httpErrorResponse: e,
          products,
          data,
        });
      } else {
        const err = e as Error;
        this._logger.error('Unable to create product sales lead.', {
          error: {
            message: err.message,
            stack: err.stack,
            name: err.name,
          },
          products,
          data,
        });
      }
    }
  }

  private async _createEloquaForSales(
    contactSalesData: IEloquaSalesLead,
  ): Promise<IEloquaSubmitResponse | undefined | null> {
    try {
      return await firstValueFrom(
        this._eloquaSalesLeadDataAccessService.createSalesLead(contactSalesData, 221, 'contactSalesData'),
      );
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        this._logger.error('Unable to contact sales.', {
          httpErrorResponse: e,
          contactSalesData,
        });
      } else {
        const err = e as Error;
        this._logger.error('Unable to contact sales.', {
          error: {
            message: err.message,
            stack: err.stack,
            name: err.name,
          },
          contactSalesData,
        });
      }
      return null;
    }
  }

  private async _createLead(): Promise<string | null | undefined> {
    const user = await firstValueFrom(this._authorizationService.principal$);
    const identity = user.identity;
    return identity.email;
  }
}
